const STORAGE_KEY = "ncell_success_transactions";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const pruneExpired = (items) => {
  const cutoff = Date.now() - ONE_DAY_MS;
  return items.filter((item) => new Date(item.savedAt).getTime() > cutoff);
};

const formatPhoneFromMsisdn = (msisdn) => {
  if (!msisdn) return "";

  let digits = String(msisdn).replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("977")) {
    digits = digits.slice(3);
  }

  return digits.length === 10 ? digits : "";
};

export const buildHistoryCopySummary = ({ phone, code, offerCode }) => {
  const lines = [];

  if (phone) {
    lines.push(phone);
  }
  if (code && offerCode) {
    lines.push(`${code} / ${offerCode}`);
  } else if (code) {
    lines.push(code);
  }

  return lines.join("\n");
};

export const getHistoryCopyText = (item) => {
  if (item?.phone && item?.code && item?.offerCode) {
    return buildHistoryCopySummary({
      phone: item.phone,
      code: item.code,
      offerCode: item.offerCode,
    });
  }

  return item?.copySummary || "";
};

export const getNcellTransactions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    const valid = pruneExpired(items);

    if (valid.length !== items.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(valid));
    }

    return valid.sort(
      (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
    );
  } catch (error) {
    return [];
  }
};

export const findNcellTransaction = (code) => {
  if (!code) return null;
  return getNcellTransactions().find((item) => item.code === code) || null;
};

const withCopySummary = (record) => ({
  ...record,
  copySummary: buildHistoryCopySummary({
    phone: record.phone,
    code: record.code,
    offerCode: record.offerCode,
  }),
});

export const upsertNcellTransaction = (record, options = {}) => {
  const { redeemOnly = false } = options;

  if (!record?.code) {
    return getNcellTransactions();
  }

  const items = getNcellTransactions();
  const existing = items.find((item) => item.code === record.code);
  let nextRecord;

  if (redeemOnly && existing) {
    nextRecord = {
      ...existing,
      status: record.status || "REDEEMED",
      savedAt: new Date().toISOString(),
    };
  } else if (existing) {
    nextRecord = {
      ...existing,
      code: record.code,
      phone: record.phone || existing.phone,
      offerCode: record.offerCode || existing.offerCode,
      partner: record.partner || existing.partner,
      status: record.status || existing.status,
      savedAt: new Date().toISOString(),
    };
  } else {
    nextRecord = {
      ...record,
      savedAt: new Date().toISOString(),
    };
  }

  const normalizedRecord = withCopySummary(nextRecord);
  const withoutDuplicate = items.filter((item) => item.code !== normalizedRecord.code);
  const updated = [normalizedRecord, ...withoutDuplicate];

  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

export const buildNcellTransactionRecord = ({ transaction, code, status, offers }) => {
  const offerList = offers || (Array.isArray(transaction?.data) ? transaction.data : []);
  const primaryOffer = offerList[0];
  const transactionCode = transaction?.transactionId || code;
  const phone = formatPhoneFromMsisdn(transaction?.msisdn);
  const offerCode = primaryOffer?.offer_code || "";

  return {
    code: transactionCode,
    phone,
    offerCode,
    partner: transaction?.partner || "",
    status: status || transaction?.status || "VALID",
  };
};
